<template>
  <div class="file-browser panel side-panel side-panel-with-curtain" :data-dragging="dragging" @click.self="cancel">
    <div
      ref="container"
      :class="['panel-container', 'is-animated', 'slide-from-right', 'shadow-elevation-3']"
      @dragover.prevent="onDragOver"
      @dragleave.prevent="onDragLeave"
      @drop.prevent="onDrop"
    >
      <div v-if="uploading" class="panel-body file-browser-uploading-body">
        <interstitial-uploader
          v-if="uploading"
          class="panel-interstitial"
          :file="file"
          :max-file-size="maxFileSize"
          @done="onUploadDone"
          @reject="onUploadReject"
          @cancel="onUploadCancel"
        />
      </div>
      <div v-else-if="dragging" class="panel-body panel-interstitial file-browser-drop-message">
        <div class="tv-cell">
          <img :src="imageSrc" width="218" height="130" />
        </div>
        <div class="tv-cell strong-type">{{ __('Drop file here to upload.') }}</div>
      </div>
      <template v-else>
        <header class="panel-header">
          <div class="panel-left-item">
            <button class="text-button narrow-button" @click="cancel">{{ __('Close') }}</button>
          </div>
          <div class="panel-title">{{ __('Files') }}</div>
          <div class="panel-right-item" />
        </header>
        <div class="panel-body file-browser-input-body">
          <label :for="'input' + _uid" class="tv file-browser-drag-message">
            <div class="tv-cell">
              <p>
                <strong>{{
                  __('Pick a file from your computer by dragging it here or by clicking the button below.')
                }}</strong>
              </p>
              <p class="caption-type small-type">
                {{
                  __(
                    'We support all kinds of files – images, videos, docs, spreadsheets, presentations, pdfs, songs, and more.',
                  )
                }}
              </p>
            </div>
            <div class="tv-cell">
              <div class="raised-button">{{ __('Pick file') }}</div>
            </div>
          </label>
          <input :id="'input' + _uid" type="file" @change="startUpload" />
          <div v-if="cabinet && existingFiles.length > 0" class="file-browser-cabinet">
            <div class="caption-type tv-cell file-browser-cabinet-title">
              {{ __('Or, select a file from your padlet drive') }}
            </div>
            <a
              v-for="url in existingFilesSubset"
              :key="url"
              :href="url"
              :style="{ width: fileWidth + 'px' }"
              class="file-browser-file"
              @click.prevent="selectFileFromCabinet"
            >
              <attachment-preview
                class="file-browser-attachment"
                :url="url"
                :actionable="false"
                :show-provider="false"
                :width="fileWidth"
              />
            </a>
          </div>
          <div ref="io" class="file-browser-observer" />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* NB
  The .prevents in drag and drop listeners are important for file drop to work. See more here:
  https://developer.mozilla.org/en-US/docs/Web/API/HTML_Drag_and_Drop_API/Drag_operations#droptargets
  (Some docs say you need to return false as well, we don't need to.)
  Also, console has bugs. Inspecting e.dataTransfer in the console will not work. Try console logging it instead.
*/
import { fetchJson } from '@padlet/fetch'
import { trackEvent } from 'bits/analytics'
import imagePath from 'images/add_attachment.svg'
import AttachmentPreview from 'vuecomponents/surface_attachment_preview'
import ContentSourceUploaderMixin from 'vuemixins/content_source_uploader_mixin'
import EscapeToCancelMixin from 'vuemixins/escape_to_cancel_mixin'

export default {
  components: { AttachmentPreview },
  mixins: [ContentSourceUploaderMixin, EscapeToCancelMixin],
  props: {
    cabinet: {
      type: Boolean,
      default: true,
    },
    maxFileSize: {
      type: Number,
    },
  },
  data() {
    return {
      imageSrc: imagePath,
      dragging: false,
      fileWidth: 175,
      lazyLoadingThreshold: 20,
      existingFilesSubset: [],
      existingFiles: [],
    }
  },
  mounted() {
    if (this.cabinet) {
      // this allows us to accommodate the scrollbar
      this.$refs.container.style.overflow = 'scroll'
      this.fileWidth = Math.floor(this.$refs.container.clientWidth / 2 - 12)
      this.$refs.container.style.overflow = 'auto'
      fetchJson('/api/attachments?filter=files').then((response) => {
        this._intersectionObserver.observe(this.$refs.io)
        this.existingFiles = response
        this.existingFilesSubset = this.existingFiles.slice(0, this.lazyLoadingThreshold)
      })
      // using default options: browser viewport and triggering when even 1px of the target shows up
      this._intersectionObserver = new IntersectionObserver(this.onIntersection.bind(this))
    }
  },
  beforeDestroy() {
    this._intersectionObserver.disconnect()
  },
  methods: {
    close() {
      this.$emit('close')
    },
    cancel() {
      if (this.uploading) {
        if (
          window.confirm(
            this.__('You are in the middle of an upload. Are you sure you want to close the file browser?'),
          )
        ) {
          this.close()
        }
      } else {
        this.close()
      }
    },
    onDragLeave() {
      this.dragging = false
    },
    onDragOver() {
      if (this.uploading) return
      this.dragging = true
    },
    onDrop(e) {
      if (this.uploading) return
      this.dragging = false
      if (e.dataTransfer) {
        trackEvent('File Browser', 'Dragged File')
        const files = e.dataTransfer.files
        if (files && files.length > 0) {
          this.handleFile(files[0])
        }
      }
    },
    onIntersection(entries) {
      const ioEntry = entries[0]
      // greater than zero means came into view
      if (ioEntry.intersectionRatio > 0) {
        if (this.existingFiles.length > this.existingFilesSubset.length) {
          const newSliceMax = this.existingFilesSubset.length + this.lazyLoadingThreshold
          this.existingFilesSubset = this.existingFiles.slice(0, newSliceMax)
        }
      }
    },
    onUploadCancel() {
      this.uploading = false
    },
    onUploadReject(file, reason) {
      this.$emit('reject', file, reason)
    },
    selectFileFromCabinet(e) {
      trackEvent('File Browser', 'Selected Existing')
      this.$emit('select', e.currentTarget.href)
    },
    startUpload(e) {
      trackEvent('File Browser', 'Picked File')
      const file = e.target.files[0]
      this.handleFile(file)
    },
  },
}
</script>

<style lang="scss" scoped>
@import './../../assets/stylesheets/3/modules/all';
.panel-container {
  transition: background-color 0.2s;
}
[data-dragging] {
  .file-browser-drop-message {
    background-color: white;
  }
}
.file-browser-uploading-body {
  background-color: white;
}
input[type='file'] {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}
.file-browser-drag-message {
  flex-grow: 0;
  flex-shrink: 0;
  background-color: white;
  margin: 8px;
  padding: 8px 0px;
  border-radius: $default-border-radius;
}
.file-browser-cabinet {
  display: flex;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 4px;
}
.file-browser-cabinet-title {
  font-size: $body-font-size;
}
.file-browser-file {
  margin: 4px;
  border-radius: $default-border-radius;
  overflow: hidden;
  background-color: white;
  color: inherit;
  display: flex;
  flex-direction: column;
}
.file-browser-observer {
  height: 16px;
  flex-shrink: 0;
}
.file-browser-attachment {
  flex-grow: 1;
}
</style>
